import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentCheckIns,
  getTournamentPlayerCheckIns,
  selectTournamentPlayerCheckIns,
  selectTournamentTeamCheckIns,
} from "../../reducers/checkinSlice";
import { useMatch } from "react-router-dom";
import { getRoundName } from "../round/roundHooks";

export const teamFilter = (teams, search) => {
  return teams?.filter?.((t) =>
    `${t?.nameOrContact} ${t?.players?.map?.((p) => p.name)?.join(" ")}`
      ?.toLowerCase?.()
      .includes?.(search?.toLowerCase?.()),
  );
};

export const useTournamentCheckins = (load = false) => {
  const dispatch = useDispatch();

  const match = useMatch("/:tournamentID/*");
  const tournamentID = match?.params?.tournamentID;

  const tournamentCheckIns = useSelector(selectTournamentTeamCheckIns);
  const tournamentPlayerCheckIns = useSelector(selectTournamentPlayerCheckIns);

  useEffect(() => {
    if (tournamentID && load) {
      dispatch(getTournamentCheckIns(tournamentID));
      dispatch(getTournamentPlayerCheckIns(tournamentID));
    }
  }, [tournamentID, dispatch, load]);

  const mapCheckinsToTeams = (checkins, search = "") => {
    const teams = checkins.map?.((tci) => {
      const playerCheckins = tournamentPlayerCheckIns?.filter?.(
        (tpci) =>
          tpci?.tournamentTeam?.id === tci?.tournamentTeam?.id &&
          tpci?.roundHoleAssignment?.id === tci?.roundHoleAssignment?.id,
      );
      return {
        ...tci.tournamentTeam,
        playerCheckins,
        roundCheckIn: tci,
        holeAssignment: tci.roundHoleAssignment,
      };
    });

    if (search) {
      return teamFilter(teams, search);
    }

    return teams;
  };

  const mapCheckinsToRounds = (teamRounds, checkins, search = "") => {
    const teamRoundCheckins = teamRounds
      ?.filter?.((tr) =>
        `${getRoundName(tr?.tournamentRound)}`.includes?.(search),
      )
      ?.map?.((tr) => {
        return {
          ...tr,
          checkIn: checkins.find?.(
            (tci) =>
              tci.roundHoleAssignment?.tournamentRound?.id ===
              tr?.tournamentRound?.id,
          ),
        };
      });

    return teamRoundCheckins;
  };

  const getRoundCheckins = (round, search = "") => {
    const playerCheckins = tournamentPlayerCheckIns.filter?.(
      (pci) => pci.roundHoleAssignment?.tournamentRound?.id === round?.id,
    );
    const roundCheckins =
      tournamentCheckIns?.filter?.(
        (tci) => tci.roundHoleAssignment?.tournamentRound?.id === round?.id,
      ) ?? [];
    const mappedCheckins = mapCheckinsToTeams(roundCheckins, search);
    const totalTeams = roundCheckins?.length;
    const teamsCheckedIn =
      roundCheckins?.filter?.((rci) => rci?.checkedInAt)?.length ?? 0;

    return {
      roundCheckins,
      mappedCheckins,
      totalTeams,
      teamsCheckedIn,
      playerCheckins,
    };
  };

  const getTeamCheckIns = (teamRounds, team, search = "") => {
    const teamCheckins = tournamentCheckIns?.filter?.(
      (tci) => tci.tournamentTeam?.id === team?.id,
    );
    const mappedCheckins = mapCheckinsToRounds(
      teamRounds,
      teamCheckins,
      search,
    );

    return { teamCheckins, mappedCheckins };
  };

  return {
    tournamentCheckIns,
    tournamentPlayerCheckIns,
    getRoundCheckins,
    mapCheckinsToTeams,
    getTeamCheckIns,
  };
};
